import { useState } from 'react';

import { ArrowLeftRight, Link, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

import AddUsers from './AddUsers';
import TransferCase from './TransferCase';
import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { Button as NewButton } from '@/components/atoms/new-button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/molecules/DropdownMenu';
import Modal from '@/components/molecules/Modals/SettingsModal';

export enum ShareOptions {
  MoveToDifferentMatter = 'MoveToDifferentMatter',
  AddUsers = 'AddUsers',
}

interface ShareProps {
  matterId?: string;
  caseId?: string;
  allowedOptions: ShareOptions[];
}

const Share = ({ matterId, caseId, allowedOptions }: ShareProps) => {
  const [matterShareModalOpen, setMatterShareModalOpen] = useState(false);
  const [addUsersModalOpen, setAddUsersModalOpen] = useState(false);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link to case copied', { duration: 1500, position: 'top-center' });
  };

  return (
    <>
      <div className="flex">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <NewButton
              size="sm"
              variant="brand"
              className="h-7 rounded-l-full rounded-r-none px-2 font-normal text-white shadow-md transition-all duration-200 hover:scale-105 hover:shadow-lg"
            >
              <UserPlus size={16} strokeWidth={2} />
              <span className="ml-1 text-sm">Share</span>
            </NewButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="mx-2 w-58 border border-gray-100 bg-white p-1 shadow-md">
            <DropdownMenuLabel className="px-2 py-1 text-xs text-gray-500">Options</DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-gray-200" />
            <DropdownMenuGroup>
              {allowedOptions.includes(ShareOptions.MoveToDifferentMatter) && caseId && (
                <DropdownMenuItem
                  onClick={() => setMatterShareModalOpen(true)}
                  className="flex items-center justify-between rounded px-2 py-1 text-sm hover:cursor-pointer hover:bg-gray-100"
                >
                  <span>Move to a different matter</span>
                  <ArrowLeftRight size={14} />
                </DropdownMenuItem>
              )}

              {allowedOptions.includes(ShareOptions.AddUsers) && (
                <DropdownMenuItem
                  onClick={() => {
                    trackCustomEvent(TrackingEvents.AddUserToCaseFromCasePage);
                    setAddUsersModalOpen(true);
                  }}
                  className="flex items-center justify-between rounded px-2 py-1 text-sm hover:cursor-pointer hover:bg-gray-100"
                >
                  <span>Add users to matter</span>
                  <UserPlus size={14} />
                </DropdownMenuItem>
              )}
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>

        <NewButton
          size="sm"
          variant="brand"
          className="h-7 rounded-l-none rounded-r-full border-l px-2 font-normal text-white shadow-md transition-all duration-200 hover:scale-105 hover:shadow-lg"
          onClick={() => {
            trackCustomEvent(TrackingEvents.CopyCaseLink);
            copyToClipboard();
          }}
          aria-label="Copy case link"
        >
          <Link size={16} strokeWidth={2} />
        </NewButton>
      </div>

      {caseId && (
        <Modal
          title={<div className="text-lg font-semibold">Transfer Case</div>}
          isOpen={matterShareModalOpen}
          handleClose={() => setMatterShareModalOpen(false)}
          size="small"
          content={<TransferCase caseId={caseId} setMatterShareModalOpen={setMatterShareModalOpen} />}
        />
      )}

      {matterId && (
        <Modal
          title={<div className="text-lg font-semibold">Add Users</div>}
          isOpen={addUsersModalOpen}
          handleClose={() => setAddUsersModalOpen(false)}
          size="small"
          content={<AddUsers matterId={matterId} />}
        />
      )}
    </>
  );
};

export default Share;
